/**
 * The paced reveal of a session's result (roadmap S-4, ruling R10): the host
 * starts it, everyone sees a three-second countdown, then the ranks appear
 * one at a time as the host advances, or all at once when the host skips.
 *
 * One hook for every screen that shows it — the host's controls, the
 * participants' result page and the TV board — so they move together. The
 * host's acts are rows in `session_events` (reveal_started, reveal_next_rank,
 * reveal_complete); each screen follows them over Realtime and, when it opens
 * mid-reveal, catches up from the rows already there.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { getSafeSupabaseClient } from '@/lib/supabaseSync';
import { emitSessionEvent } from '@/lib/sessionEvents';
import { useUserStore } from '@/stores/prioritization/userStore';

export type RevealPhase = 'idle' | 'countdown' | 'revealing' | 'complete';

export const REVEAL_COUNTDOWN_SECONDS = 3;

const REVEAL_EVENTS = ['reveal_started', 'reveal_next_rank', 'reveal_complete'] as const;

export interface RevealEventRow {
  event_type: string;
  payload: { rank?: number } | null;
  created_at?: string;
}

export interface RevealState {
  phase: RevealPhase;
  /** Seconds left in the countdown. */
  countdown: number;
  /** How many ranks are showing, from the top. */
  revealedUpTo: number;
}

/**
 * Where a reveal stands after these events (newest last). Pure, so the
 * catch-up read and the tests share it.
 */
export function revealStateFromEvents(
  events: readonly RevealEventRow[],
  itemCount: number,
  nowMs: number = Date.now(),
): RevealState {
  let lastStart = -1;
  events.forEach((e, i) => {
    if (e.event_type === 'reveal_started') lastStart = i;
  });
  if (lastStart < 0) return { phase: 'idle', countdown: REVEAL_COUNTDOWN_SECONDS, revealedUpTo: 0 };
  const since = events.slice(lastStart + 1);
  if (since.some((e) => e.event_type === 'reveal_complete')) {
    return { phase: 'complete', countdown: 0, revealedUpTo: itemCount };
  }
  const upTo = Math.max(1, ...since.map((e) => (e.event_type === 'reveal_next_rank' ? e.payload?.rank ?? 0 : 0)));
  const startedAt = events[lastStart].created_at ? Date.parse(events[lastStart].created_at!) : NaN;
  const left = Number.isFinite(startedAt)
    ? REVEAL_COUNTDOWN_SECONDS - Math.floor((nowMs - startedAt) / 1000)
    : 0;
  if (since.length === 0 && left > 0) return { phase: 'countdown', countdown: left, revealedUpTo: 0 };
  if (upTo >= itemCount && itemCount > 0) return { phase: 'complete', countdown: 0, revealedUpTo: itemCount };
  return { phase: 'revealing', countdown: 0, revealedUpTo: upTo };
}

export function useSessionReveal(sessionId: string | null | undefined, itemCount: number) {
  const userId = useUserStore((s) => s.userId) ?? '';
  const userName = useUserStore((s) => s.userName) ?? '';
  const [state, setState] = useState<RevealState>({
    phase: 'idle',
    countdown: REVEAL_COUNTDOWN_SECONDS,
    revealedUpTo: 0,
  });
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const itemCountRef = useRef(itemCount);
  itemCountRef.current = itemCount;

  const runCountdown = useCallback((from: number) => {
    if (timer.current) clearInterval(timer.current);
    let n = from;
    setState({ phase: 'countdown', countdown: n, revealedUpTo: 0 });
    timer.current = setInterval(() => {
      n -= 1;
      if (n <= 0) {
        if (timer.current) clearInterval(timer.current);
        timer.current = null;
        setState({ phase: 'revealing', countdown: 0, revealedUpTo: 1 });
      } else {
        setState((s) => ({ ...s, countdown: n }));
      }
    }, 1000);
  }, []);

  const apply = useCallback((next: RevealState) => {
    if (next.phase === 'countdown') {
      runCountdown(next.countdown);
      return;
    }
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
    setState(next);
  }, [runCountdown]);

  useEffect(() => {
    if (!sessionId) return;
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    let cancelled = false;

    // Catch up: someone who opens the page mid-reveal sees where it is.
    void (async () => {
      const { data } = await supabase
        .from('session_events')
        .select('event_type, payload, created_at')
        .eq('session_id', sessionId)
        .in('event_type', [...REVEAL_EVENTS])
        .order('created_at', { ascending: true })
        .limit(500);
      if (cancelled || !data) return;
      apply(revealStateFromEvents(data as RevealEventRow[], itemCountRef.current));
    })();

    const channel = supabase
      .channel(`reveal:${sessionId}:${Math.random().toString(36).slice(2, 8)}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'session_events', filter: `session_id=eq.${sessionId}` },
        (payload) => {
          const ev = payload.new as RevealEventRow;
          if (ev.event_type === 'reveal_started') {
            runCountdown(REVEAL_COUNTDOWN_SECONDS);
          } else if (ev.event_type === 'reveal_next_rank') {
            const rank = ev.payload?.rank ?? 0;
            if (timer.current) clearInterval(timer.current);
            timer.current = null;
            setState(
              rank >= itemCountRef.current
                ? { phase: 'complete', countdown: 0, revealedUpTo: itemCountRef.current }
                : { phase: 'revealing', countdown: 0, revealedUpTo: rank },
            );
          } else if (ev.event_type === 'reveal_complete') {
            if (timer.current) clearInterval(timer.current);
            timer.current = null;
            setState({ phase: 'complete', countdown: 0, revealedUpTo: itemCountRef.current });
          }
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      if (timer.current) clearInterval(timer.current);
      timer.current = null;
      void supabase.removeChannel(channel);
    };
  }, [sessionId, apply, runCountdown]);

  const start = useCallback(async () => {
    if (!sessionId) return;
    await emitSessionEvent(sessionId, userId, userName, 'reveal_started', {});
  }, [sessionId, userId, userName]);

  const next = useCallback(async () => {
    if (!sessionId) return;
    const rank = state.revealedUpTo + 1;
    if (rank > itemCount) return;
    await emitSessionEvent(sessionId, userId, userName, 'reveal_next_rank', { rank });
  }, [sessionId, userId, userName, state.revealedUpTo, itemCount]);

  const showAll = useCallback(async () => {
    if (!sessionId) return;
    await emitSessionEvent(sessionId, userId, userName, 'reveal_complete', {});
  }, [sessionId, userId, userName]);

  return { ...state, start, next, showAll };
}
